<template>
	<div
		v-if="questions.length > 0"
		:data-layout="layout"
		:class="['help-suggestion-chips', `help-suggestion-chips--${layout}`]"
	>
		<button
			v-for="(question, index) in questions"
			:key="`${index}-${question}`"
			type="button"
			:data-testid="testIdPrefix ? `${testIdPrefix}-${index}` : null"
			:disabled="disabled"
			class="help-suggestion-chips__chip"
			@click="$emit('select', { text: question })"
		>
			{{ question }}
		</button>
	</div>
</template>

<script>
/**
 * A set of suggested questions, as hairline text buttons (AB#27211). The Vue twin of syrenis-ui's
 * `SuggestionChip`/`SuggestionChips`.
 *
 * Underlined rather than filled on purpose: these appear in the assistant's empty state, beneath the
 * hero composer and as follow-ups mid-conversation, and a row of pills competes with the controls
 * that actually act. `column` stacks one per line, which is what the 760px sheet needs; the splash
 * hero is wide enough to wrap.
 */
export default {
	name: 'help-suggestion-chips',
	props: {
		/** Questions to offer. Renders nothing when empty. */
		questions: {
			type: Array,
			default: () => []
		},
		/** `wrap` flows chips across the width; `column` stacks one per line. */
		layout: {
			type: String,
			default: 'wrap',
			validator: value => ['wrap', 'column'].includes(value)
		},
		disabled: {
			type: Boolean,
			default: false
		},
		/** Prefix for each chip's `data-testid`, suffixed with the index. */
		testIdPrefix: {
			type: String,
			default: ''
		}
	}
}
</script>

<style lang="scss" scoped>
.help-suggestion-chips {
	display: flex;

	&--wrap {
		flex-wrap: wrap;
		gap: 8px 24px;
	}

	&--column {
		flex-direction: column;
		gap: 8px;
		align-items: flex-start;
	}

	&__chip {
		padding: 0 0 2px;
		font-size: 0.8125rem;
		font-weight: 500;
		color: var(--muted-foreground, #666E7A);
		text-align: left;
		cursor: pointer;
		background: none;
		border: none;
		border-bottom: 1px solid var(--border, #E0E2E5);
		border-radius: 0;
		transition: color 0.15s ease, border-color 0.15s ease;

		&:hover:not(:disabled) {
			color: var(--foreground, #4A5568);
			border-bottom-color: var(--foreground, #4A5568);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.5;
		}

		&:focus:not(:focus-visible) {
			outline: none;
		}

		&:focus-visible {
			outline: 2px solid var(--global-primary, #406A94);
			outline-offset: 2px;
		}
	}
}
</style>
